import { useMemo, useState } from 'react';

/**
 * Shared messages filter state + derivation. Both `MessagesPage` and
 * Profile → Messages tab use this hook so the two views ALWAYS produce
 * identical filtered/sorted results from the same threads list.
 *
 * State: activeTab ∈ {all, buyer, seller}, categoryFilter, sortOrder.
 * Returns the filtered threads (memoized) + setters + an
 * `activeFilterCount` for the Filtres button badge.
 */
export const useMessagesFilter = (threads) => {
  const [activeTab, setActiveTab] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');

  const filteredThreads = useMemo(() => {
    let list = Array.isArray(threads) ? [...threads] : [];
    if (activeTab === 'buyer') {
      list = list.filter((th) => th.is_listing_owner === false);
    } else if (activeTab === 'seller') {
      list = list.filter((th) => th.is_listing_owner === true);
    }
    if (categoryFilter !== 'all') {
      list = list.filter((th) => th.listing_category === categoryFilter);
    }
    list.sort((a, b) => {
      const aT = new Date(a.last_message_time).getTime();
      const bT = new Date(b.last_message_time).getTime();
      return sortOrder === 'oldest' ? aT - bT : bT - aT;
    });
    return list;
  }, [threads, activeTab, categoryFilter, sortOrder]);

  const activeFilterCount =
    (categoryFilter !== 'all' ? 1 : 0) + (sortOrder !== 'newest' ? 1 : 0);

  return {
    activeTab,
    setActiveTab,
    categoryFilter,
    setCategoryFilter,
    sortOrder,
    setSortOrder,
    filteredThreads,
    activeFilterCount,
  };
};
